import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import InquiryForm from "@/components/feature/InquiryForm";
import BookingCalendar from "@/components/feature/BookingCalendar";
import { photos, images } from "@/mocks/site";

const reasons = [
  { no: "01", title: "Festivals", note: "Food, culture and community stages." },
  { no: "02", title: "Culinary Events", note: "Tastings, pop-ups and long tables." },
  { no: "03", title: "Restaurant Openings", note: "Bring the room to life." },
  { no: "04", title: "Corporate Events", note: "Keynotes and team experiences." },
  { no: "05", title: "Food Experiences", note: "Markets, farms and journeys." },
  { no: "06", title: "Community Events", note: "Where food meets neighbours." },
];

export default function InviteEddiePage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Bring Eddie · Invite"
        title={
          <>
            INVITE
            <br />
            EDDIE
          </>
        }
        subtitle="An appearance, a demonstration, a conversation or an experience — tell us what you're building and where."
        image={photos.table}
        imageClassName="object-center"
      />

      <section className="bg-background-50">
        <div className="w-full px-5 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Reveal>
                <p className="eyebrow">Where Eddie Fits</p>
              </Reveal>
              <Reveal delay={120}>
                <h2 className="mt-5 font-heading text-3xl leading-[1.05] text-foreground-950 sm:text-4xl md:text-5xl">
                  A presence that{" "}
                  <span className="italic text-primary-600">brings people in.</span>
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {reasons.map((r, i) => (
              <Reveal key={r.no} delay={i * 70}>
                <div className="border-t border-foreground-950/12 pt-6">
                  <span className="font-heading text-lg italic text-primary-500">
                    {r.no}
                  </span>
                  <h3 className="mt-3 font-heading text-2xl text-foreground-950">
                    {r.title}
                  </h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">
                    {r.note}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="w-full px-5 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="aspect-[4/5] w-full overflow-hidden bg-foreground-900 image-zoom">
                  <img
                    src={images.foodPlating}
                    alt="A dish prepared for an event"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal delay={120}>
                <InquiryForm
                  title="Invite Eddie"
                  description="Fill in the details below and Eddie's team will get back to you. If a field doesn't apply yet, leave it blank."
                  submitLabel="Send Invitation"
                />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section id="booking" className="scroll-mt-20 bg-background-50">
        <div className="w-full px-5 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="eyebrow">Book a Time</p>
              </Reveal>
              <Reveal delay={120}>
                <h2 className="mt-5 font-heading text-3xl leading-[1.05] text-foreground-950 md:text-4xl">
                  Talk it through{" "}
                  <span className="italic text-primary-600">with Eddie's team.</span>
                </h2>
              </Reveal>
              <Reveal delay={200}>
                <p className="mt-6 font-body text-base leading-relaxed text-foreground-700">
                  Pick a time that suits you and we'll walk through the details
                  of your invitation — the when, the where and the story behind
                  it.
                </p>
              </Reveal>
              <Reveal delay={280}>
                <ul className="mt-8 space-y-3">
                  {[
                    "Choose from live availability",
                    "Confirm in a couple of minutes",
                    "A real conversation, no scripts",
                  ].map((point) => (
                    <li
                      key={point}
                      className="flex items-start gap-3 font-body text-sm text-foreground-700"
                    >
                      <i className="ri-check-line mt-0.5 text-base text-primary-600" />
                      {point}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <Reveal delay={120}>
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/1e7e4cf6-af0d-4f68-a1ae-f4eb7d8865a2.f866d4819dc01aae35fa49548153e610fb486e9bf8fe9869afd6d298349f6ffd"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/1e7e4cf6-af0d-4f68-a1ae-f4eb7d8865a2.f866d4819dc01aae35fa49548153e610fb486e9bf8fe9869afd6d298349f6ffd"
                  texts={{
                    eyebrow: "Booking",
                    heading: "Choose a time to talk",
                    subheading:
                      "Select a date, pick a slot and tell us who you are — we'll take it from there.",
                    occasionLabel: "What's the occasion?",
                    occasionPlaceholder: "Select an occasion",
                    occasionOptions: [
                      "Festival",
                      "Culinary Event",
                      "Restaurant Opening",
                      "Corporate Event",
                      "Private Event",
                      "Media & Press",
                      "Other",
                    ],
                    notesLabel: "Anything we should know?",
                    notesPlaceholder:
                      "Tell us the shape of the event and what you'd love Eddie to do (500 characters max).",
                    submit: "Confirm booking",
                  }}
                />
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}